"use client";
import React from "react";
import Link from "next/link";
import type { LeadConsentState } from "@/utils/hooks/useLeadConsent";

// Shown instead of the checkbox when DPDP hides it (is_displayed=false).
const NOTICE_TEXT = "By proceeding, you confirm and agree to the {Privacy Policy} and {Terms & Conditions}.";

// Maps a {placeholder} in the DPDP display_text to its policy page.
const policyHref = (label: string): string | null => {
    const key = label.toLowerCase();
    if (key.includes("privacy")) return "/privacy-policy";
    if (key.includes("terms")) return "/terms-and-condition";
    return null;
};

// Renders text with {Placeholder} segments as policy links.
const renderDisplayText = (text: string, newTab: boolean) =>
    text.split(/(\{[^}]+\})/).map((part, i) => {
        const match = part.match(/^\{([^}]+)\}$/);
        if (!match) return <React.Fragment key={i}>{part}</React.Fragment>;
        const label = match[1];
        const href = policyHref(label);
        if (!href) return <React.Fragment key={i}>{label}</React.Fragment>;
        return (
            <Link
                key={i}
                prefetch={false}
                href={href}
                className="text-[#008C8B] hover:underline"
                {...(newTab && { target: "_blank", rel: "noopener noreferrer" })}
            >
                {label}
            </Link>
        );
    });

interface Props {
    consent: LeadConsentState;
    className?: string;
}

/** DPDP consent checkbox; falls back to the plain notice when DPDP hides the checkbox. */
const LeadConsent: React.FC<Props> = ({ consent, className = "" }) => {
    const { config, checked, setChecked } = consent;

    if (!config.is_displayed) {
        return (
            <p className={`text-[12px] text-[#90a1b9] text-center pt-1 ${className}`}>
                {renderDisplayText(NOTICE_TEXT, false)}
            </p>
        );
    }

    return (
        <label className={`flex items-start gap-2 text-[12px] text-[#62748e] cursor-pointer text-left ${className}`}>
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="mt-[2px] h-4 w-4 shrink-0 cursor-pointer accent-[#008C8B]"
                data-testid="lead-consent-checkbox"
            />
            {/* New tab so following a policy link doesn't throw away the open form. */}
            <span>{renderDisplayText(config.display_text, true)}</span>
        </label>
    );
};

export default LeadConsent;
